import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { buildSuggestions, normalizeQuery } from "../../lib/productSearch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

// Customer product search (Sep 2026).
//
// Mode-aware by design, and the BACKEND decides the mode: the public
// catalog payload carries `catalog.search_mode`, so the threshold at
// which a catalog switches from in-browser filtering to a server query
// is tunable in production without a frontend release.
//
//   "client" — the whole catalog is already in the payload, so
//              filtering is instant, costs no network round trip, and
//              keeps working on a flaky kiosk connection.
//   "server" — the payload is a capped page; suggestions come from
//              `/catalog/search`, which queries the FULL catalog so no
//              product is ever unreachable.
//
// VOICE READINESS — deliberately structured so the future microphone
// phase needs no new search path: this hook takes a plain string and
// has no idea where it came from. A transcript calls `setQuery` and the
// identical suggestion flow runs. Two rules are baked in here so that
// phase cannot go wrong: the hook NEVER mutates the cart, and selecting
// a result is always an explicit customer confirmation (this hook only
// ever returns candidates — it performs no action of its own).
//
// Debounce applies ONLY to the network path; client-side filtering is
// synchronous so the list updates on the keystroke with no flicker.
export const SERVER_DEBOUNCE_MS = 220;
export const SERVER_MIN_CHARS = 2;
export const CLIENT_MIN_CHARS = 1;

export default function useProductSearch({
  slug, catalogId, linkSlug, mode = "client", products = [], limit = 8,
  // Product Family scope (Sep 2026). Only the SERVER path needs it: the
  // client path is already handed a family-scoped `products` list, so
  // passing it there would filter the same set twice. Null/omitted is
  // the ALL tab and sends no parameter at all, which is exactly what
  // every pre-existing caller does.
  family = null,
}) {
  const [query, setQuery] = useState("");
  const [serverSuggestions, setServerSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // A facet chip the customer applied from a Brand / Product Family
  // suggestion. Held here (not in the menu) so clearing search restores
  // the catalog in one action.
  const [facet, setFacet] = useState(null);
  const requestRef = useRef(0);

  const trimmed = normalizeQuery(query);
  const minChars = mode === "server" ? SERVER_MIN_CHARS : CLIENT_MIN_CHARS;
  const active = trimmed.length >= minChars;

  // Client path — synchronous, no network, no debounce.
  const clientSuggestions = useMemo(() => {
    if (mode !== "client" || !active) return [];
    return buildSuggestions(products, trimmed, limit);
  }, [mode, active, products, trimmed, limit]);

  // Server path — debounced, and every in-flight response is stamped
  // with a request id so a slow earlier response can never overwrite a
  // newer one (the classic type-ahead race).
  useEffect(() => {
    if (mode !== "server") return undefined;
    if (!active) {
      setServerSuggestions([]);
      setLoading(false);
      return undefined;
    }
    if (!slug) return undefined;
    const id = ++requestRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      const params = { q: trimmed, limit };
      if (catalogId) params.catalog_id = catalogId;
      if (linkSlug) params.link_slug = linkSlug;
      if (family) params.family = family;
      axios
        .get(`${RETAIL_BASE}/${slug}/catalog/search`, { params })
        .then((r) => {
          if (id !== requestRef.current) return;
          setServerSuggestions(r.data?.suggestions || []);
        })
        .catch(() => {
          if (id !== requestRef.current) return;
          // A failed suggestion lookup is never fatal — the customer can
          // still browse. Fail quiet, not with a blocking error.
          setServerSuggestions([]);
        })
        .finally(() => {
          if (id === requestRef.current) setLoading(false);
        });
    }, SERVER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, active, slug, catalogId, linkSlug, trimmed, limit, family]);

  const suggestions = mode === "server" ? serverSuggestions : clientSuggestions;

  const clear = useCallback(() => {
    requestRef.current += 1;
    setQuery("");
    setServerSuggestions([]);
    setFacet(null);
    setOpen(false);
    setLoading(false);
  }, []);

  const applyFacet = useCallback((suggestion) => {
    setFacet({
      type: suggestion.type,
      value: suggestion.value,
      value_id: suggestion.value_id || null,
      value_normalized:
        suggestion.value_normalized ||
        String(suggestion.value || "").toLowerCase().replace(/\s+/g, " ").trim(),
      count: suggestion.count,
    });
    setQuery("");
    setServerSuggestions([]);
    setOpen(false);
  }, []);

  return {
    query, setQuery, trimmed, active,
    suggestions, loading, open, setOpen,
    facet, applyFacet, clearFacet: () => setFacet(null),
    clear,
    // True when the customer has narrowed the menu in any way, so the
    // shell knows to pause scroll-spy and show a "clear" affordance.
    filtering: active || !!facet,
  };
}

// Opening a product that is not in the loaded browse page (Sep 2026).
//
// On a catalog larger than the storefront cap the browse payload is a
// PAGE while Search queries the FULL collection, so a suggestion can
// legitimately name a product whose card was never sent. Fetching the
// authoritative row is the only correct answer: the suggestion carries
// display text and a display price, and building a card out of that
// would put an unverified price in front of a customer and an
// unverified id into the cart.
//
// Stale-response safety: every request carries a nonce and a late
// response from an earlier pick is discarded, so a slow first tap can
// never overwrite the product the customer chose second.
export function useSelectedProduct({ slug, catalogId, linkSlug }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  const clear = useCallback(() => {
    requestRef.current += 1;
    setProduct(null);
    setError(null);
    setLoading(false);
  }, []);

  // Returns the loaded card, or `null` when the product could not be
  // opened. The CALLER decides what to do next — in particular it must
  // not clear the customer's query until this resolves, so a failed
  // lookup leaves their words on screen to try again.
  const open = useCallback(async (suggestion) => {
    if (!suggestion || !suggestion.id || !slug) return null;
    const id = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (catalogId) params.catalog_id = catalogId;
      if (linkSlug) params.link_slug = linkSlug;
      const { data } = await axios.get(
        `${RETAIL_BASE}/${slug}/catalog/product/${encodeURIComponent(suggestion.id)}`,
        { params },
      );
      if (id !== requestRef.current) return null;
      const card = data?.product || null;
      setProduct(card);
      return card;
    } catch (_e) {
      if (id !== requestRef.current) return null;
      // A 404 here is the correct answer for a product the operator has
      // since withdrawn, or an id that does not belong to this tenant
      // and catalog. Either way the customer is told, never left with a
      // silent dead tap.
      setProduct(null);
      setError(suggestion.label || "That item");
      return null;
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [slug, catalogId, linkSlug]);

  return { product, loading, error, open, clear };
}

// Choosing a Product suggestion (Sep 2026).
//
// Extracted so the page and its tests run the SAME code — the ordering
// rules below are the whole correction, and a second copy in a test
// harness would let them drift apart unnoticed.
//
// Two cases, and neither may end in a dead tap:
//
//   * the card is already on the loaded page -> clear the query so the
//     full menu is back, then reveal and highlight it;
//   * the card is NOT on the page (large, capped catalog) -> fetch the
//     authoritative product FIRST and clear the query only once it is
//     on screen. A failed lookup therefore leaves the customer's own
//     words to try again, and the query is never cleared into nothing.
//
// Membership of the loaded page is decided from the browse payload, not
// from the DOM, so the decision never depends on render timing.
export function createProductSelectionHandler({
  allProducts, search, selected, onReveal,
}) {
  return async function selectProduct(suggestion) {
    if (!suggestion || !suggestion.id) return null;
    const onPage = (allProducts || []).some((p) => p.id === suggestion.id);
    if (onPage) {
      selected.clear();
      search.clear();
      onReveal({ id: suggestion.id, nonce: Date.now() + Math.random() });
      return suggestion.id;
    }
    const card = await selected.open(suggestion);
    // `selected.error` drives the notice; do NOT clear the query here.
    if (!card) return null;
    search.clear();
    onReveal({ id: card.id, nonce: Date.now() + Math.random() });
    return card.id;
  };
}

// Browse by Brand / by Product Family (Sep 2026).
//
// Always served by the BACKEND, in both catalog sizes and deliberately:
// the browse payload is capped, so filtering the in-memory page would
// silently hide products past the cap — exactly the thing the approved
// design forbids. The endpoint paginates (`has_more` + `total`), so a
// Brand with 900 products yields all 900 across pages.
export function useFacetBrowse({
  slug, catalogId, linkSlug, facet, pageSize = 60,
  // Product Family scope (Sep 2026) — a Brand chip opened from inside a
  // family tab stays inside that family, so a control the customer
  // reached from within RESTAURANT can never answer with a Grocery
  // product.
  family = null,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const fetchPage = useCallback(async (offset, append) => {
    if (!facet || !slug) return;
    const id = ++requestRef.current;
    setLoading(true);
    try {
      const params = {
        by: facet.type === "brand" ? "brand" : "product",
        limit: pageSize,
        offset,
      };
      if (facet.value_id) params.value_id = facet.value_id;
      else params.value = facet.value;
      if (catalogId) params.catalog_id = catalogId;
      if (linkSlug) params.link_slug = linkSlug;
      if (family) params.family = family;
      const { data } = await axios.get(`${RETAIL_BASE}/${slug}/catalog/browse`, { params });
      if (id !== requestRef.current) return;
      setItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (_e) {
      if (id !== requestRef.current) return;
      // Browsing a facet is an enhancement; a failure must never blank
      // the menu. The customer keeps the catalog they already have.
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [facet, slug, catalogId, linkSlug, pageSize, family]);

  useEffect(() => {
    if (!facet) {
      requestRef.current += 1;
      setItems([]);
      setTotal(0);
      setHasMore(false);
      setLoading(false);
      return;
    }
    fetchPage(0, false);
  }, [facet, fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    fetchPage(items.length, true);
  }, [hasMore, loading, items.length, fetchPage]);

  return { items, total, hasMore, loading, loadMore };
}
